import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine
} from "recharts";
import {
  Activity, ArrowRight, ArrowLeft, CheckCircle, ChevronLeft, TrendingUp, TrendingDown
} from "lucide-react";

import { API } from "../lib/api";

function getScoreColor(s) {
  if (s >= 80) return "#10B981";
  if (s >= 60) return "#F59E0B";
  if (s >= 40) return "#F97316";
  return "#EF4444";
}

export default function PulseCheckPage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/pulse/questions`, { withCredentials: true })
      .then(({ data }) => setQuestions(data))
      .catch(console.error);
    axios.get(`${API}/api/pulse/history`, { withCredentials: true })
      .then(({ data }) => {
        setHistory(data);
        if (data.length > 0) setShowHistory(true);
      })
      .catch(console.error);
  }, []);

  const totalQ = questions.length;
  const answeredCount = Object.keys(responses).length;
  const allAnswered = totalQ > 0 && answeredCount === totalQ;
  const currentQuestion = questions[currentQ];

  const handleAnswer = (qId, label) => {
    setResponses((prev) => ({ ...prev, [qId]: label }));
    setTimeout(() => {
      if (currentQ < totalQ - 1) setCurrentQ(currentQ + 1);
    }, 250);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/pulse`, { responses }, { withCredentials: true });
      setResult(data);
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const chartData = [...history].reverse().map((p) => ({
    week: p.week,
    score: p.pulse_score,
    date: new Date(p.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
  }));

  const trend = history.length >= 2 ? history[0].pulse_score - history[1].pulse_score : 0;

  // ── Result Screen ──
  if (result) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-12">
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center animate-slide-up">
            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
            <h2 className="font-outfit text-2xl font-black text-slate-900 mb-2">Pulse Check Complete</h2>
            <p className="font-ibm-plex text-sm text-slate-500 mb-6">Week {result.week}</p>
            <div className="mb-6">
              <span className="font-outfit text-6xl font-black" style={{ color: getScoreColor(result.pulse_score) }}>
                {result.pulse_score}
              </span>
              <span className="font-ibm-plex text-lg text-slate-400 ml-1">/ 100</span>
            </div>
            <p className="font-ibm-plex text-sm text-slate-500 mb-8">
              {result.pulse_score >= 80 ? "Team is in great shape this week." :
               result.pulse_score >= 60 ? "Moderate energy — some areas need attention." :
               result.pulse_score >= 40 ? "Below average — several areas are struggling." :
               "Critical — immediate attention required."}
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => navigate("/dashboard")}
                className="border border-gray-300 text-slate-700 font-outfit font-semibold px-6 py-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                Dashboard
              </button>
              <button onClick={() => navigate("/project/new")}
                className="bg-slate-900 text-white font-outfit font-semibold px-6 py-2.5 rounded-lg hover:bg-slate-800 transition-colors">
                Full Assessment
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── History + Start Screen ──
  if (showHistory && answeredCount === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-8">
          <div className="mb-8 animate-slide-up">
            <button onClick={() => navigate("/dashboard")}
              className="flex items-center gap-1 text-sm font-ibm-plex text-slate-500 hover:text-slate-900 mb-4 transition-colors">
              <ChevronLeft className="w-4 h-4" /> Dashboard
            </button>
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-5 h-5 text-blue-600" />
              <h1 className="font-outfit text-3xl font-black text-slate-900">Weekly Pulse</h1>
            </div>
            <p className="font-ibm-plex text-sm text-slate-500">
              {history.length} pulse check{history.length !== 1 ? "s" : ""} recorded
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6 animate-slide-up stagger-1">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400 mb-1">Latest</p>
              <p className="font-outfit text-3xl font-black" style={{ color: getScoreColor(history[0]?.pulse_score || 0) }}>
                {history[0]?.pulse_score || "—"}
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400 mb-1">Average</p>
              <p className="font-outfit text-3xl font-black text-slate-700">
                {history.length ? Math.round(history.reduce((s, p) => s + p.pulse_score, 0) / history.length) : "—"}
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400 mb-1">Trend</p>
              <div className="flex items-center gap-1">
                {trend > 0 ? <TrendingUp className="w-5 h-5 text-emerald-500" /> :
                 trend < 0 ? <TrendingDown className="w-5 h-5 text-red-500" /> : null}
                <span className={`font-outfit text-2xl font-black ${trend > 0 ? "text-emerald-600" : trend < 0 ? "text-red-600" : "text-slate-400"}`}>
                  {trend > 0 ? `+${trend.toFixed(0)}` : trend.toFixed(0)}
                </span>
              </div>
            </div>
          </div>

          {/* Trend Chart */}
          {chartData.length >= 2 && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 animate-slide-up stagger-2">
              <h2 className="font-outfit text-base font-bold text-slate-900 mb-4">Pulse Trend</h2>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                  <XAxis dataKey="date" tick={{ fontFamily: "IBM Plex Sans", fontSize: 11, fill: "#9CA3AF" }} />
                  <YAxis domain={[0, 100]} tick={{ fontFamily: "IBM Plex Sans", fontSize: 11, fill: "#9CA3AF" }} />
                  <Tooltip contentStyle={{ fontFamily: "IBM Plex Sans", fontSize: 12 }} formatter={(v) => [`${v}/100`, "Pulse"]} />
                  <ReferenceLine y={80} stroke="#10B981" strokeDasharray="4 4" strokeOpacity={0.4} />
                  <ReferenceLine y={60} stroke="#F59E0B" strokeDasharray="4 4" strokeOpacity={0.4} />
                  <Line type="monotone" dataKey="score" stroke="#3B82F6" strokeWidth={2} dot={{ fill: "#3B82F6", r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <button data-testid="start-pulse-btn" onClick={() => setShowHistory(false)}
            className="w-full bg-slate-900 text-white font-outfit font-semibold py-3.5 rounded-lg hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 animate-slide-up stagger-3">
            <Activity className="w-4 h-4" /> Start This Week's Pulse <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ── Pulse Questions (one per screen) ──
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      {/* Progress */}
      <div className="sticky top-14 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="font-outfit text-sm font-bold text-blue-600 flex items-center gap-1.5">
              <Activity className="w-4 h-4" /> Weekly Pulse
            </span>
            <span className="font-outfit text-sm font-bold text-slate-900">{answeredCount}/{totalQ}</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full transition-all duration-300"
              style={{ width: `${totalQ > 0 ? (answeredCount / totalQ) * 100 : 0}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {currentQuestion && (
          <div className="animate-slide-up" key={currentQuestion.id}>
            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
              <span className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400 mb-4 block">
                Question {currentQ + 1} of {totalQ}
              </span>
              <h2 className="font-outfit text-xl font-bold text-slate-900 leading-snug mb-8"
                data-testid="pulse-question-text">
                {currentQuestion.question}
              </h2>
              <div className="space-y-3">
                {currentQuestion.options.map((opt) => {
                  const isSelected = responses[currentQuestion.id] === opt.label;
                  return (
                    <button key={opt.label} data-testid={`pulse-opt-${currentQuestion.id}-${opt.label}`}
                      onClick={() => handleAnswer(currentQuestion.id, opt.label)}
                      className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all flex items-start gap-4 group ${
                        isSelected ? "border-blue-600 bg-blue-600 text-white" : "border-gray-200 hover:border-blue-200 hover:bg-blue-50 text-slate-700"
                      }`}>
                      <span className={`flex-shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-outfit font-bold ${
                        isSelected ? "border-white text-white" : "border-gray-300 text-slate-400 group-hover:border-blue-300"
                      }`}>{opt.label}</span>
                      <span className="font-ibm-plex text-base leading-relaxed pt-1">{opt.text}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between mt-6">
              <button onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0}
                className="flex items-center gap-2 font-outfit font-semibold text-sm text-slate-600 hover:text-slate-900 disabled:opacity-30 transition-colors">
                <ArrowLeft className="w-4 h-4" /> Previous
              </button>
              {currentQ === totalQ - 1 && allAnswered ? (
                <button data-testid="submit-pulse-btn" onClick={handleSubmit} disabled={submitting}
                  className="bg-blue-600 text-white font-outfit font-semibold px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-60">
                  {submitting ? "Submitting..." : <>Submit Pulse <CheckCircle className="w-4 h-4" /></>}
                </button>
              ) : (
                <button onClick={() => setCurrentQ(Math.min(totalQ - 1, currentQ + 1))} disabled={currentQ === totalQ - 1}
                  className="flex items-center gap-2 font-outfit font-semibold text-sm text-slate-600 hover:text-slate-900 disabled:opacity-30 transition-colors">
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
